import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  InputAdornment,
  TableSortLabel,
  Tooltip,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  LinearProgress
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Favorite as FavoriteFilledIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
  CloudDownload as CloudDownloadIcon,
  ContentCopy as ContentCopyIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Inventory2Outlined as InventoryIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { usePaginationWithUrl } from '../../../hooks/usePaginationWithUrl';

// ✅ Import services
import { 
  getProductsPaginated,
  deleteProduct, 
  downloadTemplate,
  exportProducts,
  validateImportFile,
  importProducts,
  getProductsByFilters
} from '../services/productService';
import { useDispatch, useSelector } from 'react-redux';
import ProductWishlistDialog from '../../../components/ProductWishlistDialog';

// ✅ Import Redux actions for caching
import { fetchProductCategories } from '../../../app/store/slices/productCategorySlice';
import { fetchSuppliers } from '../../../app/store/slices/supplierSlice';
import { fetchProductBrands } from '../../../app/store/slices/productBrandSlice';

// Custom pagination actions component with first, previous, next, and last page buttons
const TablePaginationActions = (props) => {
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: { xs: 0, sm: 2.5 }, display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
        sx={{
          '&:hover': { 
            backgroundColor: 'primary.light',
            color: 'primary.main'
          }
        }}
      >
        <FirstPageIcon />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
        sx={{
          '&:hover': { 
            backgroundColor: 'primary.light',
            color: 'primary.main'
          }
        }}
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
        sx={{
          '&:hover': { 
            backgroundColor: 'primary.light',
            color: 'primary.main'
          }
        }}
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
        sx={{
          '&:hover': { 
            backgroundColor: 'primary.light',
            color: 'primary.main'
          }
        }}
      >
        <LastPageIcon />
      </IconButton>
    </Box>
  );
};

const ProductsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  // ✅ Get cached data from Redux store
  const { categories: categoriesFromStore, loading: categoriesLoading } = useSelector(state => state.productCategories || { categories: [], loading: false });
  const { suppliers: suppliersFromStore, loading: suppliersLoading } = useSelector(state => state.suppliers || { suppliers: [], loading: false });
  const { brands: brandsFromStore, loading: brandsLoading } = useSelector(state => state.productBrands || { brands: [], loading: false });
  
  // ✅ Updated state for pagination
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  // ✅ Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  
  // ✅ Sorting state (backend sorting)
  const [sortBy, setSortBy] = useState('Name');
  const [sortDirection, setSortDirection] = useState('Asc');
  
  // ✅ Pagination state with URL sync
  const { page, pageSize: rowsPerPage, handlePageChange, handlePageSizeChange, resetPage } = usePaginationWithUrl({
    defaultPage: 0,
    defaultPageSize: 10,
    pageSizeOptions: [10, 20, 50, 100]
  });
  
  // ✅ View dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // ✅ Wishlist dialog state
  const [wishlistProduct, setWishlistProduct] = useState(null);

  // ✅ Import/Export state
  const [importDialog, setImportDialog] = useState({
    open: false,
    file: null,
    fileName: '',
    loading: false,
    validating: false,
    validationResult: null,
    error: null,
    successMessage: null,
    importSuccess: false
  });

  const [exportDialog, setExportDialog] = useState({
    open: false,
    includeInventory: false,
    loading: false
  });

  // ✅ NEW: Prevent duplicate API calls
  const [isFetching, setIsFetching] = useState(false);
  
  // ✅ Use data from Redux store (no local state needed)
  const categories = categoriesFromStore;
  const brands = brandsFromStore;
  const suppliers = suppliersFromStore;

  // ✅ Fetch products with pagination and filters
  const fetchProducts = async () => {
    // Prevent duplicate concurrent calls
    if (isFetching) return;
    
    setIsFetching(true);
    setLoading(true);
    try {
      const response = await getProductsPaginated({
        pageNumber: page + 1, // Convert from 0-based to 1-based
        pageSize: rowsPerPage,
        searchTerm: debouncedSearchTerm || null,
        categoryId: categoryFilter || null,
        brandId: brandFilter || null,
        supplierId: supplierFilter || null,
        stockStatus: null,
        sortBy: sortBy,
        sortDirection: sortDirection,
        suppressErrorToast: true
      });
      
      
      // ✅ Handle paginated response structure - FIXED MAPPING
      let productData = [];
      let total = 0;
      
      if (response?.payload) {
        // ✅ Correct structure: response.payload.Items (capital I)
        productData = response.payload.Items || response.payload.items || [];
        total = response.payload.TotalItems || response.payload.totalItems || 0;
        
      } else if (response?.Items) {
        // Alternative structure
        productData = response.Items;
        total = response.TotalItems || 0;
        
      } else if (response?.items) {
        // Lowercase alternative
        productData = response.items;
        total = response.totalCount || response.totalItems || 0;
        
      } else if (Array.isArray(response)) {
        // Direct array
        productData = response;
        total = response.length;
        
      }
      
      // ✅ Map product data to match component expectations
      const mappedProducts = productData.map(product => ({
        Id: product.Id,
        Name: product.Name,
        SKU: product.SKU || product.BarCode, // Fallback to BarCode if SKU not present
        BarCode: product.BarCode,
        Description: product.Description,
        SalePrice: product.SalePrice,
        CostPrice: product.CostPrice,
        MinSalePrice: product.MinSalePrice,
        AveragePrice: product.AveragePrice,
        StockQuantity: product.StockQuantity,
        MinStockLevel: product.MinStockLevel,
        IsActive: product.IsActive,
        IsPromotionProduct: product.IsPromotionProduct,
        PromotionPercentage: product.PromotionPercentage,
        // ✅ Extract nested category name
        CategoryName: product.ProductCategory?.CategoryName || product.CategoryName,
        CategoryId: product.ProductCategoryId || product.CategoryId,
        // ✅ Extract nested supplier name
        SupplierName: product.Supplier?.Name || product.SupplierName,
        SupplierId: product.SupplierId,
        // ✅ Extract nested brand name (if available)
        BrandName: product.ProductBrand?.BrandName || product.BrandName,
        BrandId: product.ProductBrandId || product.BrandId,
        // Additional fields
        ModelNumber: product.ModelNumber,
        Location: product.Location,
        ImageUrl: product.ImageUrl,
        SyncToShopify: product.SyncToShopify,
        IsWishlistProduct: product.IsWishlistProduct,
        CreatedDate: product.CreatedDate,
        ModifiedDate: product.ModifiedDate
      }));
      
      setProducts(mappedProducts);
      setTotalCount(total);
      
    } catch (error) {

      setProducts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  // ✅ Fetch products when filters, sorting, or pagination changes
  useEffect(() => {
    fetchProducts();
  }, [page, rowsPerPage, sortBy, sortDirection, debouncedSearchTerm, categoryFilter, brandFilter, supplierFilter]);

  // ✅ Load categories, suppliers, brands from Redux store (cached)
  useEffect(() => {
    // Check if data exists in store, if not fetch from API
    if (categoriesFromStore.length === 0 && !categoriesLoading) {
      dispatch(fetchProductCategories({ suppressErrorToast: true }));
    }
    
    if (suppliersFromStore.length === 0 && !suppliersLoading) {
      dispatch(fetchSuppliers({ suppressErrorToast: true }));
    }
    
    if (brandsFromStore.length === 0 && !brandsLoading) {
      dispatch(fetchProductBrands({ suppressErrorToast: true }));
    }
  }, []); // Only run once on mount

  // ✅ Debounced search - Update debouncedSearchTerm after 1500ms delay
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to first page on search change
      if (searchTerm !== debouncedSearchTerm && page !== 0) {
        resetPage();
      }
    }, 1500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  // ✅ Handle sort change
  const handleSort = (property) => {
    const isAsc = sortBy === property && sortDirection === 'Asc';
    setSortDirection(isAsc ? 'Desc' : 'Asc');
    setSortBy(property);
    resetPage(); // Reset to first page when sorting
  };

  // ✅ Pagination handlers
  // ✅ Pagination handlers now provided by hook
  // (handlePageChange and handlePageSizeChange from usePaginationWithUrl)

  // ✅ Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setBrandFilter('');
    setSupplierFilter('');
    resetPage();
  };

  // ✅ Copy to clipboard handler
  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
  };

  // ✅ View action handler
  const handleView = (product, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    navigate(`/products/detail/${product.Id}`, {
      state: { productData: product, returnUrl: location.pathname + location.search }
    });
  };

  // ✅ Edit Handler
  const handleEdit = (product, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    navigate(`/products/edit/${product.Id}`, {
      state: { 
        productData: product,
        timestamp: Date.now(),
        returnUrl: location.pathname + location.search
      }
    });
  };

  // ✅ Add new product handler
  const handleAddNew = () => {
    navigate('/products/add');
  };

  // ✅ Delete handler
  const handleDelete = async (productId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProduct(productId, { suppressErrorToast: true });
        
        // ✅ Refresh current page
        fetchProducts();
        
      } catch (error) {
      }
    }
  };

  // ✅ Refresh handler
  const handleRefresh = () => {
    fetchProducts();
  };

  // ✅ Add to wishlist handler
  const handleAddToWishlist = (product) => {
    if (product.IsWishlistProduct) return; // already in wishlist — no-op
    setWishlistProduct(product);
  };

  // ✅ Wishlist success handler
  const handleWishlistSuccess = (product) => {
    // Update IsWishlistProduct in local products state immediately
    setProducts(prev => prev.map(p =>
      p.Id === product.Id ? { ...p, IsWishlistProduct: true } : p
    ));
  };

  // ✅ Download Template Handler
  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadTemplate({ suppressErrorToast: true });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Product_Import_Template_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
    }
  };

  // ✅ Open Export Dialog
  const handleOpenExportDialog = () => {
    setExportDialog({
      open: true,
      includeInventory: false,
      loading: false
    });
  };

  // ✅ Close Export Dialog
  const handleCloseExportDialog = () => {
    setExportDialog({
      open: false,
      includeInventory: false,
      loading: false
    });
  };

  // ✅ Export Products Handler
  const handleExportProducts = async () => {
    try {
      setExportDialog(prev => ({ ...prev, loading: true }));
      
      const blob = await exportProducts(exportDialog.includeInventory, { suppressErrorToast: true });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = exportDialog.includeInventory 
        ? `Products_with_Inventory_${new Date().toISOString().split('T')[0]}.xlsx`
        : `Products_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      handleCloseExportDialog();
    } catch (error) {
      setExportDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // ✅ Open Import Dialog
  const handleOpenImportDialog = () => {
    setImportDialog({
      open: true,
      file: null,
      fileName: '',
      loading: false,
      validating: false,
      validationResult: null,
      error: null
    });
  };

  // ✅ Close Import Dialog
  const handleCloseImportDialog = () => {
    // Refresh products if import was successful
    if (importDialog.importSuccess) {
      fetchProducts();
    }
    
    setImportDialog({
      open: false,
      file: null,
      fileName: '',
      loading: false,
      validating: false,
      validationResult: null,
      error: null,
      successMessage: null,
      importSuccess: false
    });
  };

  // ✅ Handle File Selection
  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    
    if (!file) return;

    const errors = [];
    
    if (!file.name.endsWith('.xlsx')) {
      errors.push('Only .xlsx files are supported');
    }
    
    if (file.size > 10 * 1024 * 1024) {
      errors.push('File size exceeds 10MB limit');
    }

    if (errors.length > 0) {
      setImportDialog(prev => ({
        ...prev,
        file: null,
        fileName: '',
        error: errors.join('. '),
        validationResult: null,
        successMessage: null
      }));
      return;
    }

    setImportDialog(prev => ({
      ...prev,
      file,
      fileName: file.name,
      error: null,
      validationResult: null,
      successMessage: null
    }));
  };

  // ✅ Handle Validate Products
  const handleValidateProducts = async () => {
    if (!importDialog.file) {
      setImportDialog(prev => ({ ...prev, error: 'Please select a file first' }));
      return;
    }

    try {
      setImportDialog(prev => ({ ...prev, validating: true, error: null, successMessage: null }));
      
      const result = await validateImportFile(importDialog.file, { suppressErrorToast: true });
      
      setImportDialog(prev => ({
        ...prev,
        validating: false,
        validationResult: result,
        error: null
      }));
    } catch (error) {

      const errorMessage = error.response?.data?.message || 'File validation failed';
      
      setImportDialog(prev => ({
        ...prev,
        validating: false,
        validationResult: null,
        error: errorMessage
      }));
    }
  };

  // ✅ Import Products Handler
  const handleImportProducts = async () => {
    if (!importDialog.file) {
      setImportDialog(prev => ({ ...prev, error: 'Please select a file first' }));
      return;
    }

    try {
      setImportDialog(prev => ({ ...prev, loading: true, error: null, successMessage: null }));
      
      const result = await importProducts(importDialog.file, { suppressErrorToast: true });
      
      if (result.success || result.status === 200) {
        const payload = result.payload || result.data || {};
        const created = payload.created || payload.Created || payload.successCount || 0;
        const updated = payload.updated || payload.Updated || 0;
        const totalSuccess = created + updated;
        
        setImportDialog(prev => ({
          ...prev,
          loading: false,
          successMessage: `Successfully imported ${created} and updated ${updated} products`,
          error: null,
          importSuccess: true,
          validationResult: null
        }));
      } else {
        setImportDialog(prev => ({
          ...prev,
          loading: false,
          error: result.message || 'Import failed'
        }));
      }
    } catch (error) {

      const errorMessage = error.response?.data?.message || 'Failed to import products';
      setImportDialog(prev => ({
        ...prev,
        loading: false,
        error: errorMessage
      }));
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 1, sm: 2 }, mb: 4, px: { xs: 1, sm: 2, md: 3 }, width: '100%', minWidth: 0 }}>
      {/* ? Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 600,
            color: 'primary.main',
            fontSize: { xs: '1.75rem', sm: '2.125rem' }
          }}
        >
          {t("product_list")}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            onClick={handleRefresh}
            disabled={loading}
            sx={{ textTransform: 'none', flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          >
            {t('refresh')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudDownloadIcon />}
            onClick={handleDownloadTemplate}
            sx={{ textTransform: 'none', flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          >
            {t('download_template')}
          </Button>
          {/*
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleOpenExportDialog}
            sx={{ textTransform: 'none', flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          >
            {t('export_products')}
          </Button>
          */}
          <Button
            variant="outlined"
            startIcon={<FileUploadIcon />}
            onClick={handleOpenImportDialog}
            sx={{ textTransform: 'none', flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          >
            {t('import_products')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{ 
              minWidth: { xs: 0, sm: 140 },
              flex: { xs: '1 1 100%', sm: '0 0 auto' },
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            {t("add_product")}
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(220px, 2fr) repeat(3, minmax(130px, 1fr)) auto' }, gap: 1, alignItems: 'center' }}>
          <Box sx={{ minWidth: 0, width: '100%' }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('search_by_name_sku_barcode')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')} edge="end">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>
          <Box sx={{ minWidth: 0, width: '100%' }}>
            <FormControl fullWidth size="small" sx={{ position: 'relative' }}>
              <InputLabel>{t('category') || 'Category'}</InputLabel>
              <Select
                value={categoryFilter}
                label={t('category') || 'Category'}
                onChange={(e) => { setCategoryFilter(e.target.value); resetPage(); }}
                sx={{ pr: categoryFilter ? 5 : undefined }}
              >
                <MenuItem value=""><em>{t('all_categories') || 'All Categories'}</em></MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.Id} value={c.Id}>{c.CategoryName}</MenuItem>
                ))}
              </Select>
              {categoryFilter && (
                <Tooltip title={t('clear_category') || 'Clear category'}>
                  <IconButton
                    size="small"
                    aria-label={t('clear_category') || 'Clear category'}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => { setCategoryFilter(''); resetPage(); }}
                    sx={{ position: 'absolute', right: 28, top: '50%', transform: 'translateY(-50%)', zIndex: 1, p: 0.5 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 0, width: '100%' }}>
            <FormControl fullWidth size="small" sx={{ position: 'relative' }}>
              <InputLabel>{t('brand') || 'Brand'}</InputLabel>
              <Select
                value={brandFilter}
                label={t('brand') || 'Brand'}
                onChange={(e) => { setBrandFilter(e.target.value); resetPage(); }}
                sx={{ pr: brandFilter ? 5 : undefined }}
              >
                <MenuItem value=""><em>{t('all_brands') || 'All Brands'}</em></MenuItem>
                {brands.map((b) => (
                  <MenuItem key={b.Id} value={b.Id}>{b.BrandName}</MenuItem>
                ))}
              </Select>
              {brandFilter && (
                <Tooltip title={t('clear_brand') || 'Clear brand'}>
                  <IconButton
                    size="small"
                    aria-label={t('clear_brand') || 'Clear brand'}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => { setBrandFilter(''); resetPage(); }}
                    sx={{ position: 'absolute', right: 28, top: '50%', transform: 'translateY(-50%)', zIndex: 1, p: 0.5 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 0, width: '100%' }}>
            <FormControl fullWidth size="small" sx={{ position: 'relative' }}>
              <InputLabel>{t('supplier') || 'Supplier'}</InputLabel>
              <Select
                value={supplierFilter}
                label={t('supplier') || 'Supplier'}
                onChange={(e) => { setSupplierFilter(e.target.value); resetPage(); }}
                sx={{ pr: supplierFilter ? 5 : undefined }}
              >
                <MenuItem value=""><em>{t('all_suppliers') || 'All Suppliers'}</em></MenuItem>
                {suppliers.map((s) => (
                  <MenuItem key={s.Id} value={s.Id}>{s.Name}</MenuItem>
                ))}
              </Select>
              {supplierFilter && (
                <Tooltip title={t('clear_supplier') || 'Clear supplier'}>
                  <IconButton
                    size="small"
                    aria-label={t('clear_supplier') || 'Clear supplier'}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => { setSupplierFilter(''); resetPage(); }}
                    sx={{ position: 'absolute', right: 28, top: '50%', transform: 'translateY(-50%)', zIndex: 1, p: 0.5 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </FormControl>
          </Box>
          {(searchTerm || categoryFilter || brandFilter || supplierFilter) && (
            <Tooltip title={t('clear_filters') || 'Clear Filters'}>
              <IconButton onClick={handleClearFilters} size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Active filter chips (left) + product count (right) */}
        <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'space-between' }, alignItems: 'center', mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
            {(searchTerm || categoryFilter || brandFilter || supplierFilter) && (
              <FilterListIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            )}
            {searchTerm && (
              <Chip
                label={`${t('search') || 'Search'}: "${searchTerm}"`}
                size="small"
                onDelete={() => setSearchTerm('')}
              />
            )}
            {categoryFilter && (
              <Chip
                label={`${t('category') || 'Category'}: ${categories.find(c => c.Id === categoryFilter)?.CategoryName}`}
                size="small"
                onDelete={() => { setCategoryFilter(''); resetPage(); }}
              />
            )}
            {brandFilter && (
              <Chip
                label={`${t('brand') || 'Brand'}: ${brands.find(b => b.Id === brandFilter)?.BrandName}`}
                size="small"
                onDelete={() => { setBrandFilter(''); resetPage(); }}
              />
            )}
            {supplierFilter && (
              <Chip
                label={`${t('supplier') || 'Supplier'}: ${suppliers.find(s => s.Id === supplierFilter)?.Name}`}
                size="small"
                onDelete={() => { setSupplierFilter(''); resetPage(); }}
              />
            )}
          </Box>
          <Chip
            label={`${totalCount} ${t('products')}`}
            color="primary"
            variant="outlined"
            icon={<FilterListIcon />}
            sx={{ flexShrink: 0 }}
          />
        </Box>
      </Paper>

      {/* ? Products Table with Server-side Sorting */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden'
        }}
      >
        {loading ? (
            <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  {[1, 2, 3, 4, 5, 6, 7].map((col) => (
                    <TableCell key={col} sx={{ py: 2 }}>
                      <Skeleton variant="text" width="80%" height={24} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3, 4, 5].map((row) => (
                  <TableRow key={row}>
                    {[1, 2, 3, 4, 5, 6, 7].map((col) => (
                      <TableCell 
                        key={col} 
                        sx={{ 
                          py: 1.5,
                          ...(col < 7 && { borderRight: '1px solid rgba(224, 224, 224, 0.4)' })
                        }}
                      >
                        <Skeleton variant="text" width="90%" height={20} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <>
            <TableContainer sx={{ borderRadius: 3, overflowX: 'auto' }}>
              <Table sx={{ minWidth: 820, '& th:first-of-type, & td:first-of-type': { width: { xs: 300, sm: 280 }, minWidth: { xs: 300, sm: 280 } } }}>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        fontWeight: 700,
                        backgroundColor: 'background.paper',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        py: 1.5,
                        borderRight: '1px solid rgba(224, 224, 224, 0.4)'
                      }}
                    >
                      <TableSortLabel
                        active={sortBy === 'Name'}
                        direction={sortBy === 'Name' ? sortDirection.toLowerCase() : 'asc'}
                        onClick={() => handleSort('Name')}
                      >
                        {t("product_name")}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell 
                      sx={{ 
                        fontWeight: 700,
                        backgroundColor: 'background.paper',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        py: 1.5,
                        borderRight: '1px solid rgba(224, 224, 224, 0.4)'
                      }}
                    >
                      <TableSortLabel
                        active={sortBy === 'SKU'}
                        direction={sortBy === 'SKU' ? sortDirection.toLowerCase() : 'asc'}
                        onClick={() => handleSort('SKU')}
                      >
                        SKU
                      </TableSortLabel>
                    </TableCell>

                    <TableCell 
                      sx={{ 
                        fontWeight: 700,
                        backgroundColor: 'background.paper',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        py: 1.5,
                        borderRight: '1px solid rgba(224, 224, 224, 0.4)'
                      }}
                    >
                      {t("category")}
                    </TableCell>

                    <TableCell 
                      sx={{ 
                        fontWeight: 700,
                        backgroundColor: 'background.paper',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        py: 1.5,
                        borderRight: '1px solid rgba(224, 224, 224, 0.4)',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <TableSortLabel
                        active={sortBy === 'Price'}
                        direction={sortBy === 'Price' ? sortDirection.toLowerCase() : 'asc'}
                        onClick={() => handleSort('Price')}
                      >
                        {t("unit_price")}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell 
                      sx={{ 
                        fontWeight: 700,
                        backgroundColor: 'background.paper',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        py: 1.5,
                        borderRight: '1px solid rgba(224, 224, 224, 0.4)'
                      }}
                    >
                      <TableSortLabel
                        active={sortBy === 'Stock'}
                        direction={sortBy === 'Stock' ? sortDirection.toLowerCase() : 'asc'}
                        onClick={() => handleSort('Stock')}
                      >
                        {t("stock")}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell 
                      sx={{ 
                        fontWeight: 700,
                        backgroundColor: 'background.paper',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        py: 1.5,
                        borderRight: '1px solid rgba(224, 224, 224, 0.4)'
                      }}
                    >
                      {t("status")}
                    </TableCell>

                    <TableCell 
                      sx={{ 
                        fontWeight: 700,
                        backgroundColor: 'background.paper',
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        py: 1.5
                      }} 
                      align="center"
                    >
                      {t("actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((product, index) => (
                    <TableRow 
                      key={product.Id} 
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        },
                        backgroundColor: index % 2 === 0 ? 'background.paper' : 'action.hover',
                        cursor: 'pointer'
                      }}
                    >
                      <TableCell sx={{ py: 1.5, borderRight: '1px solid rgba(224, 224, 224, 0.4)', width: { xs: 300, sm: 280 }, minWidth: { xs: 300, sm: 280 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <Tooltip title={product.Name} placement="top" arrow>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 500,
                                color: 'text.primary',
                                fontSize: '0.9375rem',
                                letterSpacing: '0.01em',
                                lineHeight: 1.5,
                                display: '-webkit-box',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'normal',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: { xs: 5, sm: 3 },
                                wordBreak: 'break-word',
                                minWidth: 0,
                                flex: 1,
                                transition: 'color 0.2s',
                                '&:hover': {
                                  color: 'primary.main'
                                }
                              }}
                            >
                              {product.Name}
                            </Typography>
                          </Tooltip>
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopy(product.Name, t('product_name'));
                            }}
                            sx={{ 
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              '.MuiTableRow-root:hover &': {
                                opacity: 0.6
                              },
                              '&:hover': { 
                                opacity: 1,
                                backgroundColor: 'primary.light',
                                color: 'primary.main'
                              },
                              flexShrink: 0
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 1.5, borderRight: '1px solid rgba(224, 224, 224, 0.4)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: 'monospace',
                              backgroundColor: 'grey.100',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              fontWeight: 500,
                              fontSize: '0.875rem'
                            }}
                          >
                            {product.SKU}
                          </Typography>
                          {product.SKU && (
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopy(product.SKU, t('sku'));
                              }}
                              sx={{ 
                                opacity: 0.5, 
                                '&:hover': { 
                                  opacity: 1,
                                  backgroundColor: 'primary.light',
                                  color: 'primary.main'
                                } 
                              }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 1.5, borderRight: '1px solid rgba(224, 224, 224, 0.4)' }}>
                        {product.CategoryName ? (
                          <Chip
                            label={product.CategoryName}
                            size="small"
                            variant="filled"
                            color="primary"
                            sx={{ 
                              borderRadius: 2,
                              fontWeight: 500,
                              fontSize: '0.8125rem'
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            {t("no_category")}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.5, borderRight: '1px solid rgba(224, 224, 224, 0.4)' }}>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 600,
                            color: 'success.main',
                            fontSize: '1rem'
                          }}
                        >
                          €{product.SalePrice?.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5, borderRight: '1px solid rgba(224, 224, 224, 0.4)' }}>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 600,
                            fontSize: '1rem'
                          }}
                        >
                          {product.StockQuantity || 0}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5, borderRight: '1px solid rgba(224, 224, 224, 0.4)' }}>
                        <Chip 
                          label={product.IsActive ? t("active") : t("inactive")} 
                          size="small" 
                          color={product.IsActive ? 'success' : 'default'}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            fontSize: '0.8125rem'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1.5 }}>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title={product.IsWishlistProduct ? t('in_wishlist') || 'In Wishlist' : t('add_to_wishlist')}>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleAddToWishlist(product)}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'secondary.light'
                                }
                              }}
                            >
                              {product.IsWishlistProduct
                                ? <FavoriteFilledIcon fontSize="small" />
                                : <FavoriteBorderIcon fontSize="small" />
                              }
                            </IconButton>
                          </Tooltip>
                          
                          <IconButton
                            size="small"
                            color="info"
                            onClick={(event) => handleView(product, event)}
                            title={t("view_product_details")}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'info.light'
                              }
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(event) => handleEdit(product, event)}
                            title={t("edit_product")}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'primary.light'
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(event) => handleDelete(product.Id, event)}
                            title={t("delete_product")}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'error.light'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 20, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handlePageSizeChange}
              ActionsComponent={TablePaginationActions}
              sx={{
                borderTop: '2px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                '.MuiTablePagination-toolbar': {
                  py: 1,
                  px: { xs: 1, sm: 2 },
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  justifyContent: { xs: 'center', sm: 'flex-end' }
                },
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  fontWeight: 500
                }
              }}
            />
          </>
        )}

        {/* ? Enhanced Empty State */}
        {!loading && products.length === 0 && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            py: 10,
            gap: 3
          }}>
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                backgroundColor: 'primary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}
            >
              <InventoryIcon sx={{ fontSize: 64, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" fontWeight={600} color="text.primary">
              {searchTerm || categoryFilter || brandFilter || supplierFilter
                ? t("no_products_found")
                : t("no_products_available")}
            </Typography>
            {searchTerm || categoryFilter || brandFilter || supplierFilter ? (
              <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: 400 }}>
                Try adjusting your search or filter criteria to find what you're looking for.
              </Typography>
            ) : (
              <>
                <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: 500 }}>
                  {t("get_started_by_adding_your_first_product")}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={handleAddNew}
                  sx={{ 
                    textTransform: 'none',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2
                  }}
                >
                  {t("add_first_product")}
                </Button>
              </>
            )}
            {(searchTerm || categoryFilter || brandFilter || supplierFilter) && (
              <Button
                variant="outlined"
                size="large"
                onClick={handleClearFilters}
                sx={{ 
                  textTransform: 'none',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2
                }}
              >
                {t("clear_filters")}
              </Button>
            )}
          </Box>
        )}
      </Paper>

      {/* ? View Product Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("product_details")}</DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">{t("product_name")}</Typography>
                <Typography variant="body1">{selectedProduct.Name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">{t("sku")}</Typography>
                <Typography variant="body1">{selectedProduct.SKU}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 1.5, sm: 3 }, py: 2, gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' }, '& > button': { width: { xs: '100%', sm: 'auto' } } }}>
          <Button onClick={() => setViewDialogOpen(false)}>{t("close")}</Button>
          <Button onClick={() => { setViewDialogOpen(false); handleEdit(selectedProduct); }} variant="contained">
            {t("edit")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ? Add to Wishlist Dialog */}
      <ProductWishlistDialog
        open={Boolean(wishlistProduct)}
        onClose={() => setWishlistProduct(null)}
        product={wishlistProduct}
        onSuccess={handleWishlistSuccess}
      />

      {/* ✅ Export Dialog */}
      <Dialog 
        open={exportDialog.open} 
        onClose={handleCloseExportDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FileDownloadIcon color="primary" />
              <Typography variant="h6">Export Products</Typography>
            </Box>
            <IconButton onClick={handleCloseExportDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Export all products to an Excel file (.xlsx)
            </Typography>
            
            <FormControl component="fieldset" sx={{ mt: 2 }} size="small">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={exportDialog.includeInventory}
                  onChange={(e) => setExportDialog(prev => ({ ...prev, includeInventory: e.target.checked }))}
                  style={{ marginRight: '8px' }}
                />
                <Typography variant="body2">
                  Include inventory details (stock quantities, min stock levels)
                </Typography>
              </Box>
            </FormControl>

            <Alert severity="info" sx={{ mt: 3 }}>
              The exported file will contain all product data in Excel format.
            </Alert>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: { xs: 1.5, sm: 3 }, pt: 1, gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' }, '& > button': { width: { xs: '100%', sm: 'auto' } } }}>
          <Button 
            onClick={handleCloseExportDialog}
            disabled={exportDialog.loading}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleExportProducts}
            disabled={exportDialog.loading}
            startIcon={exportDialog.loading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
          >
            {exportDialog.loading ? 'Exporting...' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ? Import Dialog */}
      <Dialog 
        open={importDialog.open} 
        onClose={handleCloseImportDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FileUploadIcon color="primary" />
              <Typography variant="h6">Import Products</Typography>
            </Box>
            <IconButton onClick={handleCloseImportDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ pt: 3 }}>
          {/* Instructions */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Import Instructions:
            </Typography>
            <Typography variant="body2" component="div">
              • Download the template first and fill in your product data<br />
              • Only .xlsx files are supported (max 10MB)<br />
              • Products with existing SKU will be updated<br />
              • Products without SKU will be created as new
            </Typography>
          </Alert>

          {/* File Upload Area */}
          <Box sx={{ 
            border: '2px dashed',
            borderColor: importDialog.file ? 'success.main' : 'grey.300',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            bgcolor: importDialog.file ? 'success.50' : 'grey.50',
            mb: 3,
            transition: 'all 0.3s'
          }}>
            <input
              accept=".xlsx"
              style={{ display: 'none' }}
              id="import-file-upload"
              type="file"
              onChange={handleFileChange}
              disabled={importDialog.loading || importDialog.validating}
            />
            <label htmlFor="import-file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<FileUploadIcon />}
                disabled={importDialog.loading || importDialog.validating}
                sx={{ mb: 2 }}
              >
                Select File
              </Button>
            </label>
            
            {importDialog.fileName && (
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                ?? {importDialog.fileName}
              </Typography>
            )}
            
            {!importDialog.fileName && (
              <Typography variant="body2" color="text.secondary">
                Click to select an Excel file (.xlsx)
              </Typography>
            )}
          </Box>

          {/* Validation Status */}
          {importDialog.validating && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <CircularProgress size={24} />
              <Typography variant="body2">Validating file...</Typography>
            </Box>
          )}

          {/* Validation Result */}
          {importDialog.validationResult && (
            <Paper 
              elevation={3}
              sx={{ 
                mb: 3, 
                p: 3, 
                bgcolor: importDialog.validationResult.payload?.InvalidRows > 0 ? '#fef3c7' : '#d1fae5',
                border: '2px solid',
                borderColor: importDialog.validationResult.payload?.InvalidRows > 0 ? '#f59e0b' : '#10b981'
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937', mb: 1 }}>
                  ? Validation Results
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  {importDialog.validationResult.payload?.Summary || 'File validated'}
                </Typography>
              </Box>

              {importDialog.validationResult.payload && (
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#3b82f6' }}>
                        {importDialog.validationResult.payload.TotalRows || 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        Total
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981' }}>
                        {importDialog.validationResult.payload.ValidRows || 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        Valid
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#06b6d4' }}>
                        {importDialog.validationResult.payload.WouldCreate || 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        Create
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#8b5cf6' }}>
                        {importDialog.validationResult.payload.WouldUpdate || 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        Update
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#ef4444' }}>
                        {importDialog.validationResult.payload.InvalidRows || 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        Invalid
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                        {(importDialog.validationResult.payload.DuplicatesInExcel || 0) + (importDialog.validationResult.payload.DuplicatesInDatabase || 0)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        Duplicates
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}

              {importDialog.validationResult.payload?.Issues && importDialog.validationResult.payload.Issues.length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Issues Found:</Typography>
                  {importDialog.validationResult.payload.Issues.slice(0, 10).map((issue, idx) => (
                    <Typography key={idx} variant="caption" component="div">
                      • {issue.Reason} (Count: {issue.Count})
                    </Typography>
                  ))}
                  {importDialog.validationResult.payload.Issues.length > 10 && (
                    <Typography variant="caption" component="div" sx={{ mt: 1, fontStyle: 'italic' }}>
                      ... and {importDialog.validationResult.payload.Issues.length - 10} more issues
                    </Typography>
                  )}
                </Alert>
              )}
            </Paper>
          )}

  {/* Success Display */}
  {importDialog.successMessage && (
    <Alert severity="success" sx={{ mb: 3 }}>
      {importDialog.successMessage}
    </Alert>
  )}

        </DialogContent>
        
        <DialogActions sx={{ p: { xs: 1.5, sm: 3 }, pt: 1, gap: 1.5, justifyContent: { xs: 'stretch', sm: 'space-between' }, flexDirection: { xs: 'column-reverse', sm: 'row' }, '& > button': { width: { xs: '100%', sm: 'auto' } } }}>
          <Button 
            onClick={handleCloseImportDialog}
            disabled={importDialog.loading || importDialog.validating}
          >
            Cancel
          </Button>
          <Box sx={{ display: 'flex', gap: 1.5, width: { xs: '100%', sm: 'auto' }, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button 
              variant="outlined"
              onClick={handleValidateProducts}
              disabled={!importDialog.file || importDialog.loading || importDialog.validating}
              startIcon={importDialog.validating ? <CircularProgress size={20} /> : <SearchIcon />}
            >
              {importDialog.validating ? 'Validating...' : 'Validate Products'}
            </Button>
            <Button 
              variant="contained"
              onClick={handleImportProducts}
              disabled={
                !importDialog.file || 
                importDialog.loading || 
                importDialog.validating || 
                !importDialog.validationResult || 
                (importDialog.validationResult?.payload?.InvalidRows > 0) ||
                !!importDialog.error
              }
              startIcon={importDialog.loading ? <CircularProgress size={20} /> : <FileUploadIcon />}
            >
              {importDialog.loading ? 'Importing...' : 'Import Products'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default ProductsListPage;


